import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'motion/react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle, Loader2 } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning' | 'loading';

export interface ToastMessage {
    id: string;
    type: ToastType;
    message: string;
    duration?: number;
    progress?: number; // 0-100 for loading toasts
}

interface ToastProps {
    key?: React.Key;
    toast: ToastMessage;
    onDismiss: (id: string) => void;
}

export const Toast: React.FC<ToastProps> = ({ toast, onDismiss }) => {
    const [isHovered, setIsHovered] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const startProgressRef = useRef<number>(0);
    const remainingTimeRef = useRef<number>(toast.type === 'loading' && !toast.duration ? 60000 : toast.duration || 4000);

    useEffect(() => {
        const startTimer = () => {
            // Apply fallback timeout for loading toasts as a safeguard against Ghost Toasts
            startProgressRef.current = Date.now();
            timeoutRef.current = setTimeout(() => {
                onDismiss(toast.id);
            }, remainingTimeRef.current);
        };

        const stopTimer = () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                const elapsed = Date.now() - startProgressRef.current;
                remainingTimeRef.current -= elapsed;
            }
        };

        if (!isHovered) {
            startTimer();
        } else {
            stopTimer();
        }

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [isHovered, toast.type, onDismiss, toast.id]);

    const icons = {
        success: <CheckCircle size={18} className="text-green-400" />,
        error: <AlertCircle size={18} className="text-red-400" />,
        info: <Info size={18} className="text-blue-400" />,
        warning: <AlertTriangle size={18} className="text-yellow-400" />,
        loading: <Loader2 size={18} className="text-green-400 animate-spin" />
    };

    const bgColors = {
        success: 'bg-green-900/20 border-green-900/50',
        error: 'bg-red-900/20 border-red-900/50',
        info: 'bg-blue-900/20 border-blue-900/50',
        warning: 'bg-yellow-900/20 border-yellow-900/50',
        loading: 'bg-green-900/20 border-green-900/50'
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            className={`flex flex-col gap-2 px-4 py-3 rounded-lg border backdrop-blur-md shadow-xl min-w-[300px] pointer-events-auto ${bgColors[toast.type]}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="flex items-center gap-3">
                {icons[toast.type]}
                <p className="flex-1 text-sm font-medium text-gray-200">{toast.message}</p>
                {toast.type !== 'loading' && (
                    <button
                        onClick={() => onDismiss(toast.id)}
                        className="p-1 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white"
                        aria-label="Dismiss notification"
                    >
                        <X size={14} />
                    </button>
                )}
            </div>
            {toast.type === 'loading' && toast.progress !== undefined && (
                <div className="w-full bg-gray-800 rounded-full h-1.5 overflow-hidden">
                    <motion.div
                        className="h-full bg-green-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${toast.progress}%` }}
                        transition={{ duration: 0.3 }}
                    />
                </div>
            )}
        </motion.div>
    );
};
